import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { fetchActiveOrders, type ActiveOrder } from '../../lib/appointments';
import { updateWasserijItem } from '../../lib/wasserij-items';
import { ORDER_STATUS_COLORS } from '../../lib/order-constants';

interface Props {
  itemId: string;
  itemNumber: string;
  accountId: string;
  currentWorkOrderId: string;
  currentWorkOrderNumber: string;
  onClose: () => void;
  onMoved: () => void;
}

export function MoveWasserijItemDialog({
  itemId,
  itemNumber,
  accountId,
  currentWorkOrderId,
  currentWorkOrderNumber,
  onClose,
  onMoved,
}: Props) {
  const [orders, setOrders] = useState<ActiveOrder[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchActiveOrders(accountId)
      .then((all) => {
        if (cancelled) return;
        setOrders(all.filter((o) => o.id !== currentWorkOrderId));
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setLoadError(err.message || 'Orders laden mislukt');
      });
    return () => { cancelled = true; };
  }, [accountId, currentWorkOrderId]);

  const handleConfirm = async () => {
    if (!selectedId) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await updateWasserijItem(itemId, { Werk_Order__c: selectedId });
      onMoved();
    } catch (err) {
      const e = err as Error & { code?: string };
      setSubmitError(e.message || 'Verplaatsen mislukt');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40" onClick={submitting ? undefined : onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Verplaats naar ander werkorder</h2>
          <p className="text-xs text-slate-500 mt-1">
            {itemNumber} zit nu op {currentWorkOrderNumber}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loadError && (
            <p className="text-sm text-red-600">{loadError}</p>
          )}
          {!loadError && orders === null && (
            <p className="text-sm text-slate-500">Orders laden…</p>
          )}
          {!loadError && orders !== null && orders.length === 0 && (
            <p className="text-sm text-slate-500">
              Geen andere openstaande werkorders voor deze klant.
            </p>
          )}
          {!loadError && orders !== null && orders.length > 0 && (
            <ul className="space-y-1.5">
              {orders.map((o) => {
                const isSelected = selectedId === o.id;
                return (
                  <li key={o.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(o.id)}
                      className={clsx(
                        'w-full text-left px-3 py-2.5 rounded border flex items-center gap-3 transition-colors',
                        isSelected
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      )}
                    >
                      <input
                        type="radio"
                        checked={isSelected}
                        readOnly
                        className="text-indigo-600 focus:ring-indigo-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-900">{o.orderNumber}</span>
                          {o.brand && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">
                              {o.brand}
                            </span>
                          )}
                        </div>
                        {o.status && (
                          <span className={clsx(
                            'inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded font-medium',
                            ORDER_STATUS_COLORS[o.status] || 'bg-slate-100 text-slate-600'
                          )}>
                            {o.status}
                          </span>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-between gap-3">
          {submitError ? (
            <span className="text-xs text-red-600 truncate">{submitError}</span>
          ) : (
            <span className="text-xs text-slate-400">
              Pricing wordt automatisch herberekend.
            </span>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="text-xs text-slate-600 hover:text-slate-900 px-3 py-1.5"
            >
              Annuleren
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!selectedId || submitting}
              className={clsx(
                'px-3 py-1.5 text-xs font-medium rounded',
                !selectedId || submitting
                  ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              )}
            >
              {submitting ? 'Verplaatsen…' : 'Verplaatsen'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
